import React, { useMemo } from 'react';
import { useAsync } from 'react-async-hook';
import { commonService } from 'services';
import Space, { Spacer } from 'components/Space/Space';
import { Button, Checkbox, Divider, Dropdown, Input, Radio } from 'antd';
import { Checkmark, ChevronDown, Search } from '@carbon/icons-react';
import { ROLES } from 'constants';
import { getFullName } from 'utils';
import { useSelector } from 'react-redux';
import { selectUser } from 'features/auth';
import { startCase } from 'lodash-es';
import { UNASSIGNED_ID } from '../AssignedToCell';
import { useUrlParams } from 'hooks/useUrlParams';

const PROVIDER_IMPORT_STATUS_OPTIONS = [
  { value: 'SUCCESS', label: 'Successful' },
  { value: 'FAILURE', label: 'Failed' },
  { value: 'DUPE_FOUND', label: 'Duplicate' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'RETRYABLE', label: 'Retryable' },
];

export default function ProviderDashboardFilters({ fetchData, filters, setFilters, ClearFiltersButton }) {
  const { get: getUrlParams, update: updateUrlParams } = useUrlParams();

  const currentUser = useSelector(selectUser);
  const userFullName = [startCase(currentUser?.firstName), startCase(currentUser?.lastName)].join(' ');

  const { result: statusOptions = [] } = useAsync(async () => {
    return commonService.get('/providers/application-status-types/internal', {
      cache: { interpretHeader: false },
    });
  }, []);

  const { result: programOptions = [] } = useAsync(async () => {
    return await commonService.getAllProgramTypes();
  }, []);

  const { result: assigneeOptions = [] } = useAsync(async () => {
    return commonService.get(`/users/all?role=${ROLES.PROVIDER_COORDINATOR}`);
  }, []);

  const _thereAreFiltersApplied = useMemo(
    () =>
      filters?.statuses?.length ||
      filters?.programIds?.length ||
      filters?.importStatuses?.length ||
      filters?.assigned === false ||
      filters?.assigneeId ||
      filters?.includeTests ||
      filters?.familyWaiting !== undefined,
    [filters],
  );

  const [programIdsDropdownOpen, setProgramIdsDropdownOpen] = React.useState(false);
  const [temporaryProgramIds, setTemporaryProgramIds] = React.useState(filters?.programIds);
  const _applicationTypeFilterApplied = !!filters?.programIds?.length;

  const [statusDropdownOpen, setStatusDropdownOpen] = React.useState(false);
  const [temporaryStatuses, setTemporaryStatuses] = React.useState(filters?.statuses);
  const _statusFilterApplied = !!filters?.statuses?.length;

  const [importStatusesDropdownOpen, setImportStatusesDropdownOpen] = React.useState(false);
  const [temporaryImportStatuses, setTemporaryImportStatuses] = React.useState(filters?.importStatuses);
  const _importStatusesFilterApplied = !!filters?.importStatuses?.length;

  const [assignedToDropdownOpen, setAssignedToDropdownOpen] = React.useState(false);
  const [temporaryAssignedTo, setTemporaryAssignedTo] = React.useState(filters?.assigneeId);
  const _assignedToFilterApplied = filters?.assigneeId !== undefined || filters?.assigned === false;
  const [assignedToSearchQuery, setAssignedToSearchQuery] = React.useState('');

  const familyWaitingToString = (val) => (val === true ? 'true' : val === false ? 'false' : 'all');
  const familyWaitingFromString = (val) => (val === 'true' ? true : val === 'false' ? false : undefined);

  const [familyWaitingDropdownOpen, setFamilyWaitingDropdownOpen] = React.useState(false);
  const [temporaryFamilyWaiting, setTemporaryFamilyWaiting] = React.useState(
    familyWaitingToString(filters?.familyWaiting),
  );
  const _familyWaitingFilterApplied = filters?.familyWaiting !== undefined;

  return (
    <Space className="m-7 flex-wrap">
      {/*
       * APPLICATION TYPE filter
       */}
      {
        <Dropdown
          trigger={['click']}
          open={programIdsDropdownOpen}
          onOpenChange={(open) => {
            setProgramIdsDropdownOpen(open);
            setTemporaryProgramIds(filters?.programIds);
          }}
          dropdownRender={() => (
            <div
              className="bg-white rounded-lg"
              style={{
                boxShadow: `0 6px 16px 0 rgba(0, 0, 0, 0.08),
            0 3px 6px -4px rgba(0, 0, 0, 0.12),
            0 9px 28px 8px rgba(0, 0, 0, 0.05)`,
              }}
            >
              <div className="p-5">
                <Checkbox.Group
                  onChange={(_newValue) => {
                    setTemporaryProgramIds(_newValue);
                  }}
                  value={temporaryProgramIds}
                >
                  <Space direction="vertical" vCentered={false}>
                    {programOptions
                      ?.map((_option) => ({
                        text: _option.title,
                        value: _option.id,
                      }))
                      .map((option) => (
                        <Checkbox key={option.value} value={option.value}>
                          {option.text}
                        </Checkbox>
                      ))}
                  </Space>
                </Checkbox.Group>
              </div>

              <Divider className="m-0" />

              <div className="py-4 px-5 flex">
                <Spacer />

                <Button
                  size="small"
                  type="primary"
                  disabled={temporaryProgramIds === filters?.programIds}
                  onClick={() => {
                    setProgramIdsDropdownOpen(false);

                    const _newProgramIdsValue = temporaryProgramIds?.length ? temporaryProgramIds : undefined;
                    setFilters({
                      ...filters,
                      programIds: _newProgramIdsValue,
                    });

                    // Update URL params
                    updateUrlParams({
                      filters: {
                        ...getUrlParams()?.filters,
                        includeTests: filters?.includeTests,
                        programIds: _newProgramIdsValue?.join(','),
                      },
                    });

                    // Fetch new data
                    fetchData({
                      programIds: _newProgramIdsValue,
                    });
                  }}
                >
                  Apply
                </Button>
              </div>
            </div>
          )}
        >
          <Button
            color={_applicationTypeFilterApplied ? 'primary' : 'default'}
            variant="filled"
            data-testid="filters__programIds"
          >
            <div className="flex gap-2 items-center">
              Program
              {_applicationTypeFilterApplied && (
                <span className="w-7 h-5 rounded-full bg-primary text-white">{filters?.programIds?.length}</span>
              )}
              <ChevronDown className="mr-[-3px] mb-[-2px]" />
            </div>
          </Button>
        </Dropdown>
      }

      {/*
       * APP STATUS filter
       */}
      <Dropdown
        trigger={['click']}
        open={statusDropdownOpen}
        onOpenChange={(open) => {
          setStatusDropdownOpen(open);
          setTemporaryStatuses(filters?.statuses);
        }}
        dropdownRender={() => (
          <div
            className="bg-white rounded-lg"
            style={{
              boxShadow: `0 6px 16px 0 rgba(0, 0, 0, 0.08),
            0 3px 6px -4px rgba(0, 0, 0, 0.12),
            0 9px 28px 8px rgba(0, 0, 0, 0.05)`,
            }}
          >
            <div className="p-5">
              <Checkbox.Group
                onChange={(_newValue) => {
                  setTemporaryStatuses(_newValue);
                }}
                value={temporaryStatuses}
              >
                <Space direction="vertical" vCentered={false}>
                  {statusOptions?.map((_option) => (
                    <Checkbox key={_option.title} value={_option.title}>
                      {_option.description}
                    </Checkbox>
                  ))}
                </Space>
              </Checkbox.Group>
            </div>

            <Divider className="m-0" />

            <div className="py-4 px-5 flex">
              <Spacer />

              <Button
                size="small"
                type="primary"
                disabled={temporaryStatuses === filters?.statuses}
                onClick={() => {
                  setStatusDropdownOpen(false);

                  setFilters({
                    ...filters,
                    statuses: temporaryStatuses,
                  });

                  // Update URL params
                  updateUrlParams({
                    filters: {
                      ...getUrlParams()?.filters,
                      includeTests: filters?.includeTests,
                      statuses: temporaryStatuses?.join(','),
                    },
                  });

                  // Fetch new data
                  fetchData({
                    statuses: temporaryStatuses,
                  });
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        )}
      >
        <Button color={_statusFilterApplied ? 'primary' : 'default'} variant="filled">
          <div className="flex gap-2 items-center">
            Status
            {_statusFilterApplied && (
              <span className="w-7 h-5 rounded-full bg-primary text-white">{filters?.statuses?.length}</span>
            )}
            <ChevronDown className="mr-[-3px] mb-[-2px]" />
          </div>
        </Button>
      </Dropdown>

      {/*
       * IMPORT STATUS filter
       */}
      <Dropdown
        trigger={['click']}
        open={importStatusesDropdownOpen}
        onOpenChange={(open) => {
          setImportStatusesDropdownOpen(open);
          setTemporaryImportStatuses(filters?.importStatuses);
        }}
        dropdownRender={() => (
          <div
            className="bg-white rounded-lg"
            style={{
              boxShadow: `0 6px 16px 0 rgba(0, 0, 0, 0.08),
            0 3px 6px -4px rgba(0, 0, 0, 0.12),
            0 9px 28px 8px rgba(0, 0, 0, 0.05)`,
            }}
          >
            <div className="p-5">
              <Checkbox.Group
                onChange={(_newValue) => {
                  setTemporaryImportStatuses(_newValue);
                }}
                value={temporaryImportStatuses}
              >
                <Space direction="vertical" vCentered={false}>
                  {PROVIDER_IMPORT_STATUS_OPTIONS.map((_option) => (
                    <Checkbox key={_option.value} value={_option.value}>
                      {_option.label}
                    </Checkbox>
                  ))}
                </Space>
              </Checkbox.Group>
            </div>

            <Divider className="m-0" />

            <div className="py-4 px-5 flex">
              <Spacer />

              <Button
                size="small"
                type="primary"
                disabled={temporaryImportStatuses === filters?.importStatuses}
                onClick={() => {
                  setImportStatusesDropdownOpen(false);

                  const _newImportStatusesValue = temporaryImportStatuses?.length ? temporaryImportStatuses : undefined;
                  setFilters({
                    ...filters,
                    importStatuses: _newImportStatusesValue,
                  });

                  updateUrlParams({
                    filters: {
                      ...getUrlParams()?.filters,
                      includeTests: filters?.includeTests,
                      importStatuses: _newImportStatusesValue?.join(','),
                    },
                  });

                  fetchData({
                    importStatuses: _newImportStatusesValue,
                  });
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        )}
      >
        <Button color={_importStatusesFilterApplied ? 'primary' : 'default'} variant="filled">
          <div className="flex gap-2 items-center">
            Import Status
            {_importStatusesFilterApplied && (
              <span className="w-7 h-5 rounded-full bg-primary text-white">{filters?.importStatuses?.length}</span>
            )}
            <ChevronDown className="mr-[-3px] mb-[-2px]" />
          </div>
        </Button>
      </Dropdown>

      {/*
       * FAMILIES filter
       */}
      <Dropdown
        trigger={['click']}
        open={familyWaitingDropdownOpen}
        onOpenChange={(open) => {
          setFamilyWaitingDropdownOpen(open);
          setTemporaryFamilyWaiting(familyWaitingToString(filters?.familyWaiting));
        }}
        dropdownRender={() => (
          <div
            className="bg-white rounded-lg"
            style={{
              boxShadow: `0 6px 16px 0 rgba(0, 0, 0, 0.08),
            0 3px 6px -4px rgba(0, 0, 0, 0.12),
            0 9px 28px 8px rgba(0, 0, 0, 0.05)`,
            }}
          >
            <div className="p-5">
              <Radio.Group
                onChange={(e) => {
                  setTemporaryFamilyWaiting(e.target.value);
                }}
                value={temporaryFamilyWaiting}
              >
                <Space direction="vertical" vCentered={false}>
                  <Radio value="all">All</Radio>
                  <Radio value="true">Family Waiting</Radio>
                  <Radio value="false">No Family Waiting</Radio>
                </Space>
              </Radio.Group>
            </div>

            <Divider className="m-0" />

            <div className="py-4 px-5 flex">
              <Spacer />

              <Button
                size="small"
                type="primary"
                disabled={temporaryFamilyWaiting === familyWaitingToString(filters?.familyWaiting)}
                onClick={() => {
                  setFamilyWaitingDropdownOpen(false);

                  const _newFamilyWaitingValue = familyWaitingFromString(temporaryFamilyWaiting);
                  setFilters({
                    ...filters,
                    familyWaiting: _newFamilyWaitingValue,
                  });

                  // Update URL params
                  updateUrlParams({
                    filters: {
                      ...getUrlParams()?.filters,
                      includeTests: filters?.includeTests,
                      familyWaiting:
                        _newFamilyWaitingValue === true
                          ? 'true'
                          : _newFamilyWaitingValue === false
                          ? 'false'
                          : undefined,
                    },
                  });

                  // Fetch new data
                  fetchData({
                    familyWaiting: _newFamilyWaitingValue,
                  });
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        )}
      >
        <Button
          color={_familyWaitingFilterApplied ? 'primary' : 'default'}
          variant="filled"
          data-testid="filters__familyWaiting"
        >
          <div className="flex gap-2 items-center">
            Families
            {_familyWaitingFilterApplied && <span className="w-7 h-5 rounded-full bg-primary text-white">1</span>}
            <ChevronDown className="mr-[-3px] mb-[-2px]" />
          </div>
        </Button>
      </Dropdown>

      {/*
       * ASSIGNEE filter
       */}
      {
        <Dropdown
          trigger={['click']}
          open={assignedToDropdownOpen}
          onOpenChange={(open) => {
            setAssignedToDropdownOpen(open);
            setTemporaryAssignedTo(filters?.assigneeId);
            setAssignedToSearchQuery('');
          }}
          dropdownRender={() => (
            <div
              className="bg-white rounded-lg"
              style={{
                boxShadow: `0 6px 16px 0 rgba(0, 0, 0, 0.08),
            0 3px 6px -4px rgba(0, 0, 0, 0.12),
            0 9px 28px 8px rgba(0, 0, 0, 0.05)`,
              }}
            >
              <div className="p-2 border-b border-solid border-[#f0f0f0]">
                {/* Search Input */}
                <Input
                  placeholder="Search"
                  value={assignedToSearchQuery}
                  onChange={(e) => setAssignedToSearchQuery(e.target.value)}
                  prefix={<Search />}
                  // ref={searchInputRef}
                />
              </div>

              <div className="p-5 max-h-[300px] overflow-y-scroll">
                <Radio.Group
                  onChange={(e) => {
                    setTemporaryAssignedTo(e?.target?.value);
                  }}
                  value={
                    filters?.assigned === false && filters?.assigneeId === undefined
                      ? UNASSIGNED_ID
                      : temporaryAssignedTo
                  }
                  options={[
                    { text: 'Unassigned', value: UNASSIGNED_ID, id: UNASSIGNED_ID },
                    { text: userFullName, value: currentUser?.id, id: currentUser?.id },
                    ...(assigneeOptions || [])
                      .filter((assignee) => assignee.id !== currentUser?.id)
                      .map((assignee) => ({ text: getFullName(assignee), value: assignee.id }))
                      .sort((a, b) => a?.text?.localeCompare?.(b?.text)),
                  ]
                    ?.filter((filter) => filter.text.toLowerCase().includes(assignedToSearchQuery.toLowerCase()))
                    ?.map((_option) => ({
                      label: _option.text,
                      value: _option.value,
                    }))}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                />
              </div>

              <Divider className="m-0" />

              <div className="py-4 px-5 flex gap-2">
                <Spacer />

                <Button
                  size="small"
                  disabled={!_assignedToFilterApplied}
                  onClick={() => {
                    setAssignedToDropdownOpen(false);
                    setFilters({
                      ...filters,
                      assigned: undefined,
                      assigneeId: undefined,
                    });

                    // Update URL params
                    updateUrlParams({
                      filters: {
                        ...getUrlParams()?.filters,
                        includeTests: filters?.includeTests,
                        assigned: undefined,
                        assigneeId: undefined,
                      },
                    });

                    // Fetch new data
                    fetchData({
                      assigned: undefined,
                      assigneeId: undefined,
                    });
                  }}
                  ghost
                  danger
                >
                  Clear
                </Button>

                <Button
                  size="small"
                  type="primary"
                  disabled={temporaryAssignedTo === filters?.assigneeId}
                  onClick={() => {
                    setAssignedToDropdownOpen(false);

                    const _newFilterValue =
                      temporaryAssignedTo === UNASSIGNED_ID
                        ? { assigned: false, assigneeId: undefined }
                        : {
                            assigned: undefined,
                            assigneeId: temporaryAssignedTo,
                          };

                    setFilters({
                      ...filters,
                      ..._newFilterValue,
                    });

                    // Update URL params
                    updateUrlParams({
                      filters: {
                        ...getUrlParams()?.filters,
                        includeTests: filters?.includeTests,
                        ..._newFilterValue,
                      },
                    });

                    // Fetch new data
                    fetchData(_newFilterValue);
                  }}
                >
                  Apply
                </Button>
              </div>
            </div>
          )}
        >
          <Button color={_assignedToFilterApplied ? 'primary' : 'default'} variant="filled">
            <div className="flex gap-2 items-center">
              Assigned to
              {_assignedToFilterApplied && <span className="w-7 h-5 rounded-full bg-primary text-white">1</span>}
              <ChevronDown className="mr-[-3px] mb-[-2px]" />
            </div>
          </Button>
        </Dropdown>
      }

      {/*
       * INCLUDE TESTS filter
       */}
      {
        <Button
          color={filters?.includeTests ? 'primary' : 'default'}
          variant="filled"
          onClick={() => {
            setFilters({
              ...filters,
              includeTests: !!!filters?.includeTests,
            });

            updateUrlParams({
              filters: {
                ...getUrlParams()?.filters,
                includeTests: !!!filters?.includeTests || undefined,
              },
            });
            fetchData({
              includeTests: !!!filters?.includeTests,
            });
          }}
          aria-pressed={filters?.includeTests}
        >
          <div className="flex gap-2 items-center">
            Test accounts
            <span
              className={`w-5 h-5 border border-solid border-gray-300 rounded flex items-center justify-center pointer-events-none ${
                filters?.includeTests ? 'bg-primary border-primary' : 'bg-white'
              }`}
              role="presentation"
            >
              {filters?.includeTests && <Checkmark className="w-4 h-4 text-white" />}
            </span>
          </div>
        </Button>
      }

      {_thereAreFiltersApplied && <ClearFiltersButton data-testid="clear-filters" />}
    </Space>
  );
}
